/**
 * PSD 导入（打开真实 Photoshop 文件，ADR-0015）。跟"导出为 PSD"
 * （ADR-0014）对称——结构与常见特性尽量还原，映射不了的栅格化/占位并
 * 披露给用户，不是去啃"日常 PSD 90% 能正确显示"这个 3 人月量级的无底洞
 * （DESIGN.md §2.3）。
 *
 * 逐图层种类的还原策略：
 * - 组：结构映射，`children` 顺序反转（ag-psd 是"从上到下"，本项目数组
 *   末位在最上），`passThrough` 由 `blendMode==='pass through'` 判定。
 * - 普通像素图层：像素直接用 PSD 自己渲染好的 `imageData`（不需要重新
 *   合成），有 `mask` 就还原成 `RasterMask`，有 `effects` 就还原成
 *   `LayerEffect[]`（本项目全部 10 种效果都能渲染，值得还原成可编辑
 *   样式，不用像导出那样烧录成像素）。
 * - 文本图层：只有单一样式、无分段、非竖排、无变形这种简单情形才重建成
 *   可编辑的 `TextLayer`；复杂情形直接用 `imageData` 栅格化。
 * - 形状/智能对象（已跟用户确认）：统一栅格化，用 `imageData` 当像素。
 * - 调整图层：本项目 14 种里 13 种直接对应，`invert` 精确等价映射成
 *   `curves`（每通道取反对角线，零信息损失）；`exposure`/
 *   `selective color`/`black & white`（PS 6 滑杆模型，跟本项目 3 通道
 *   权重模型不是同一套参数）这几种插入一个恒等曲线占位（调整图层没有
 *   自己的像素，没法栅格化，只能占位）并披露——`black & white` 例外：
 *   只映射 reds/greens/blues 三个滑杆到本项目的 r/g/b（丢弃
 *   yellows/cyans/magentas），比完全不处理（画面仍是彩色）更接近原意，
 *   同样披露这是近似。
 * - 非 8 位或非 RGB 源文件：直接拒绝，不做打平硬导入——那样能打开但
 *   图层结构没有意义，跟"打开查看/微调图层"这个目标矛盾。
 *
 * 防御性解析（CLAUDE.md 规则 10，ag-psd 自己没做的部分——图层数量/嵌套
 * 深度没有上限校验，见 ADR-0015）：文件字节长度、摊平图层总数、分组
 * 嵌套深度都有上限，超出直接报错，不静默截断；`readPsd` 本身也可能因为
 * 畸形分组标记等原因抛出非预期的底层异常（比如 `stack` 被弹空的
 * `TypeError`），统一 catch 成面向用户的"文件损坏或包含不支持的特性"。
 */
import { decodePsdBytes, type PsdDecoded, type PsdDecodedLayer } from '../../io/codecs/psd';
import { createDocument, layerDefaults } from '../../document/draft';
import { maskLayerIdFor } from '../commands/mask';
import { TILE, layerId, tileKey, type LayerId } from '../../engine/tile/key';
import { BYTES_PER_TILE, type TileStore } from '../../engine/tile/store';
import type {
  Adjustment,
  BlendMode,
  Document,
  GroupLayer,
  Layer,
  LayerEffect,
  RasterLayer,
  RasterMask,
  Rect,
  TextLayer,
} from '../../document/types';

const MAX_FILE_BYTES = 500 * 1024 * 1024;
const MAX_LAYER_COUNT = 5000;
const MAX_NESTING_DEPTH = 64;

export interface ImportPsdOptions {
  readonly id: string;
  readonly fileName: string;
  readonly now: () => string;
}

export interface ImportPsdResult {
  readonly document: Document;
  /** 被降级/占位处理的图层说明，拼进打开后的提示用，不做静默降级。 */
  readonly warnings: readonly string[];
}

/* ------------------------------------------------------------------ */
/* 混合模式：反向复用导出侧建的表                                          */
/* ------------------------------------------------------------------ */

const PSD_BLEND_MODE_TO_PROJECT: Readonly<Record<string, BlendMode>> = {
  normal: 'normal',
  dissolve: 'dissolve',
  multiply: 'multiply',
  screen: 'screen',
  overlay: 'overlay',
  darken: 'darken',
  lighten: 'lighten',
  'color dodge': 'colorDodge',
  'color burn': 'colorBurn',
  'hard light': 'hardLight',
  'soft light': 'softLight',
  difference: 'difference',
  exclusion: 'exclusion',
  'linear burn': 'linearBurn',
  'linear dodge': 'linearDodge',
  'vivid light': 'vividLight',
  'linear light': 'linearLight',
  'pin light': 'pinLight',
  'hard mix': 'hardMix',
  subtract: 'subtract',
  divide: 'divide',
  hue: 'hue',
  saturation: 'saturation',
  color: 'color',
  luminosity: 'luminosity',
  'darker color': 'darkerColor',
  'lighter color': 'lighterColor',
};

function mapBlendMode(mode: string | undefined, warnings: string[], layerName: string): BlendMode {
  if (mode === undefined || mode === 'pass through') return 'normal';
  const mapped = PSD_BLEND_MODE_TO_PROJECT[mode];
  if (mapped !== undefined) return mapped;
  warnings.push(`${layerName}（未知混合模式「${mode}」，已替换成正常）`);
  return 'normal';
}

/* ------------------------------------------------------------------ */
/* 颜色                                                                 */
/* ------------------------------------------------------------------ */

/** ag-psd 的 `Color` 是多色彩模型的并集；只认 RGB/RGBA 形状，其它一律回退成给定的默认色。 */
function colorToRgb(
  color: unknown,
  fallback: readonly [number, number, number],
): readonly [number, number, number] {
  if (
    typeof color === 'object' &&
    color !== null &&
    'r' in color &&
    'g' in color &&
    'b' in color
  ) {
    const c = color as { r: number; g: number; b: number };
    return [c.r, c.g, c.b];
  }
  return fallback;
}

function rgbToHex([r, g, b]: readonly [number, number, number]): string {
  const byte = (v: number): string => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${byte(r)}${byte(g)}${byte(b)}`;
}

/* ------------------------------------------------------------------ */
/* 像素 → tile 网格（任意矩形，不像 image-import.ts 那样只处理 (0,0) 起点）  */
/* ------------------------------------------------------------------ */

/** 把 `rect` 范围内的 RGBA8 像素按 tile 网格写入 `tiles`，只触碰 rect 覆盖到的块。 */
function writeRectPixelsToTiles(
  tiles: TileStore,
  id: LayerId,
  rect: Rect,
  rgba8: Uint8Array,
): void {
  if (rect.width <= 0 || rect.height <= 0) return;
  const tileXStart = Math.floor(rect.x / TILE);
  const tileXEnd = Math.floor((rect.x + rect.width - 1) / TILE);
  const tileYStart = Math.floor(rect.y / TILE);
  const tileYEnd = Math.floor((rect.y + rect.height - 1) / TILE);

  const buffer = new Uint8Array(BYTES_PER_TILE);
  for (let ty = tileYStart; ty <= tileYEnd; ty++) {
    for (let tx = tileXStart; tx <= tileXEnd; tx++) {
      buffer.fill(0);
      const tileOriginX = tx * TILE;
      const tileOriginY = ty * TILE;
      const overlapX0 = Math.max(tileOriginX, rect.x);
      const overlapY0 = Math.max(tileOriginY, rect.y);
      const overlapX1 = Math.min(tileOriginX + TILE, rect.x + rect.width);
      const overlapY1 = Math.min(tileOriginY + TILE, rect.y + rect.height);
      const rowBytes = (overlapX1 - overlapX0) * 4;
      for (let y = overlapY0; y < overlapY1; y++) {
        const srcStart = ((y - rect.y) * rect.width + (overlapX0 - rect.x)) * 4;
        const destStart = ((y - tileOriginY) * TILE + (overlapX0 - tileOriginX)) * 4;
        buffer.set(rgba8.subarray(srcStart, srcStart + rowBytes), destStart);
      }
      // set() 内部会复制，buffer 可以复用
      tiles.set(tileKey(id, tx, ty), buffer);
    }
  }
}

/** ag-psd 的 `imageData.data` 是 `Uint8ClampedArray`（值域一致，直接重包一层）。 */
function toUint8Array(data: Uint8ClampedArray | Uint8Array | Uint16Array | Float32Array): Uint8Array {
  // useImageData:true 时 8bpc 数据是 Uint8ClampedArray（等价于 canvas ImageData.data，
  // 值域/字节布局跟 Uint8Array 完全一致，只是类型不同）——不是 instanceof Uint8Array，
  // 直接包一层视图，不逐字节拷贝
  if (data instanceof Uint8Array) return data;
  if (data instanceof Uint8ClampedArray) return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  throw new Error('像素不是 8 位数据（16/32 位 PSD 已在文档级校验时拒绝，不该走到这里）');
}

/**
 * 只支持 8 位 RGB 源文件——非目标场景（16/32 位、CMYK、灰度等，见
 * DESIGN.md §2.2 的非目标声明）打平硬导入的话图层结构没有意义，跟
 * "打开查看/微调图层"这个目标矛盾，直接拒绝更诚实。导出为独立函数是为了
 * 能在单测里直接用手工构造的 `PsdDecoded` 对象验证，不需要真的造一份
 * 16 位/非 RGB 的 PSD 字节（ag-psd 自己的写入侧目前也不支持写非 8 位）。
 */
export function assertSupportedColorMode(psd: PsdDecoded): void {
  // 3 = ColorMode.RGB（ag-psd 的 const enum，见 io/codecs/psd.ts 同款注释）
  if (psd.colorMode !== 3 || (psd.bitsPerChannel ?? 8) !== 8) {
    throw new Error('暂不支持 16/32 位或非 RGB 的 PSD，请先在 Photoshop 里转换成 8 位 RGB 再打开');
  }
}

function boundsOf(node: PsdDecodedLayer): Rect {
  const left = node.left ?? 0;
  const top = node.top ?? 0;
  const right = node.right ?? left;
  const bottom = node.bottom ?? top;
  return { x: left, y: top, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
}

/* ------------------------------------------------------------------ */
/* 图层蒙版 / 图层样式                                                    */
/* ------------------------------------------------------------------ */

function convertMask(
  node: PsdDecodedLayer,
  tiles: TileStore,
  ownerId: LayerId,
  warnings: string[],
  layerName: string,
): RasterMask | undefined {
  const mask = node.mask;
  if (mask === undefined) return undefined;
  if (mask.imageData === undefined) {
    warnings.push(`${layerName}（蒙版数据缺失，已忽略）`);
    return undefined;
  }
  const maskId = maskLayerIdFor(ownerId);
  const rect: Rect = {
    x: mask.left ?? 0,
    y: mask.top ?? 0,
    width: mask.imageData.width,
    height: mask.imageData.height,
  };
  // 蒙版数据是单通道灰度（跟 writeMaskFromSelection 同一个约定：R/G/B/A 同步写同值）
  const gray = toUint8Array(mask.imageData.data);
  const packed = new Uint8Array(rect.width * rect.height * 4);
  for (let i = 0; i < rect.width * rect.height; i++) {
    const v = gray[i]!;
    packed[i * 4] = v;
    packed[i * 4 + 1] = v;
    packed[i * 4 + 2] = v;
    packed[i * 4 + 3] = v;
  }
  writeRectPixelsToTiles(tiles, maskId, rect, packed);
  return {
    tiles: tiles.handle(maskId),
    bounds: rect,
    linked: true,
    enabled: mask.disabled !== true,
    base: (mask.defaultColor ?? 255) >= 128 ? 255 : 0,
  };
}

const EFFECT_ORDER_FROM_PSD: ReadonlyArray<{ readonly psdKey: keyof NonNullable<PsdDecodedLayer['effects']>; readonly kind: LayerEffect['kind'] }> = [
  { psdKey: 'dropShadow', kind: 'dropShadow' },
  { psdKey: 'innerShadow', kind: 'innerShadow' },
  { psdKey: 'outerGlow', kind: 'outerGlow' },
  { psdKey: 'innerGlow', kind: 'innerGlow' },
  { psdKey: 'bevel', kind: 'bevelEmboss' },
  { psdKey: 'satin', kind: 'satin' },
  { psdKey: 'solidFill', kind: 'colorOverlay' },
  { psdKey: 'gradientOverlay', kind: 'gradientOverlay' },
  { psdKey: 'patternOverlay', kind: 'patternOverlay' },
  { psdKey: 'stroke', kind: 'stroke' },
];

/** 图层效果：ag-psd 的 10 种字段跟本项目 10 种一一对应，按字段抽取塞进 `params`。 */
function convertEffects(node: PsdDecodedLayer, warnings: string[], layerName: string): readonly LayerEffect[] {
  const info = node.effects;
  if (info === undefined) return [];
  const out: LayerEffect[] = [];

  for (const { psdKey, kind } of EFFECT_ORDER_FROM_PSD) {
    const raw = info[psdKey];
    if (raw === undefined) continue;
    // dropShadow/innerShadow/solidFill/stroke 是数组（PS 支持多层同类效果叠加），
    // 本项目 `LayerEffect[]` 每种 kind 只放一份——取第一个，其余披露丢弃
    const entry = (Array.isArray(raw) ? raw[0] : raw) as Record<string, unknown> | undefined;
    if (entry === undefined) continue;
    if (Array.isArray(raw) && raw.length > 1) {
      warnings.push(`${layerName}（${kind} 效果有 ${raw.length} 份叠加，只保留第一份）`);
    }
    if (entry['enabled'] === false) continue;
    const params = effectParamsFor(kind, entry);
    if (params === null) continue;
    out.push({ kind, enabled: true, params });
  }
  return out;
}

function unitsValue(value: unknown): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'object' && value !== null && 'value' in value) {
    return (value as { value: number }).value;
  }
  return 0;
}

function effectParamsFor(
  kind: LayerEffect['kind'],
  raw: Record<string, unknown>,
): Readonly<Record<string, number | string>> | null {
  switch (kind) {
    case 'dropShadow':
    case 'innerShadow':
      return {
        color: rgbToHex(colorToRgb(raw['color'], [0, 0, 0])),
        opacity: typeof raw['opacity'] === 'number' ? (raw['opacity'] as number) : 1,
        distance: unitsValue(raw['distance']),
        angle: typeof raw['angle'] === 'number' ? (raw['angle'] as number) : 135,
        blur: unitsValue(raw['size']),
      };
    case 'outerGlow':
    case 'innerGlow':
      return {
        color: rgbToHex(colorToRgb(raw['color'], [255, 255, 0])),
        opacity: typeof raw['opacity'] === 'number' ? (raw['opacity'] as number) : 1,
        blur: unitsValue(raw['size']),
      };
    case 'bevelEmboss':
      return {
        highlightColor: rgbToHex(colorToRgb(raw['highlightColor'], [255, 255, 255])),
        shadowColor: rgbToHex(colorToRgb(raw['shadowColor'], [0, 0, 0])),
        opacity: typeof raw['highlightOpacity'] === 'number' ? (raw['highlightOpacity'] as number) : 1,
        size: unitsValue(raw['size']),
        angle: typeof raw['angle'] === 'number' ? (raw['angle'] as number) : 135,
        altitude: typeof raw['altitude'] === 'number' ? (raw['altitude'] as number) : 30,
      };
    case 'satin':
      return {
        color: rgbToHex(colorToRgb(raw['color'], [0, 0, 0])),
        opacity: typeof raw['opacity'] === 'number' ? (raw['opacity'] as number) : 1,
        distance: unitsValue(raw['distance']),
        blur: unitsValue(raw['size']),
      };
    case 'stroke':
      return {
        color: rgbToHex(colorToRgb(raw['color'], [0, 0, 0])),
        opacity: typeof raw['opacity'] === 'number' ? (raw['opacity'] as number) : 1,
        width: unitsValue(raw['size']),
        position: raw['position'] === 'inside' || raw['position'] === 'center' ? (raw['position'] as string) : 'outside',
      };
    case 'colorOverlay':
      return {
        color: rgbToHex(colorToRgb(raw['color'], [0, 0, 0])),
        opacity: typeof raw['opacity'] === 'number' ? (raw['opacity'] as number) : 1,
      };
    case 'gradientOverlay': {
      const stops = (raw['gradient'] as { colorStops?: readonly { color: unknown }[] } | undefined)?.colorStops;
      const from = stops !== undefined && stops[0] !== undefined ? colorToRgb(stops[0].color, [0, 0, 0]) : [0, 0, 0] as const;
      const last = stops !== undefined ? stops[stops.length - 1] : undefined;
      const to = last !== undefined ? colorToRgb(last.color, [255, 255, 255]) : [255, 255, 255] as const;
      return {
        from: rgbToHex(from),
        to: rgbToHex(to),
        angle: typeof raw['angle'] === 'number' ? (raw['angle'] as number) : 90,
        opacity: typeof raw['opacity'] === 'number' ? (raw['opacity'] as number) : 1,
      };
    }
    case 'patternOverlay':
      return {
        // ag-psd 的图案叠加没有单独的着色字段（本项目的实现简化成"图案+纯色着色"，
        // PSD 里没有对应字段），用默认色，已在披露列表里说明
        color: '#000000',
        opacity: typeof raw['opacity'] === 'number' ? (raw['opacity'] as number) : 1,
        scale: typeof raw['scale'] === 'number' ? (raw['scale'] as number) : 1,
        patternId: 'stripes',
      };
    default:
      return null;
  }
}

/* ------------------------------------------------------------------ */
/* 调整图层                                                              */
/* ------------------------------------------------------------------ */

function identityCurves(): Adjustment {
  return { kind: 'curves', params: { points: [0, 0, 255, 255] } };
}

function convertAdjustment(node: PsdDecodedLayer, warnings: string[], layerName: string): Adjustment {
  const adj = node.adjustment;
  if (adj === undefined) return identityCurves();

  switch (adj.type) {
    case 'curves': {
      const channel = adj.rgb;
      if (channel === undefined) {
        warnings.push(`${layerName}（曲线调整只有分通道数据，没有 RGB 复合通道，已替换成恒等曲线）`);
        return identityCurves();
      }
      const points = channel.flatMap((p) => [p.input, p.output]);
      return { kind: 'curves', params: { points } };
    }
    case 'levels': {
      const channel = adj.rgb ?? { shadowInput: 0, highlightInput: 255, shadowOutput: 0, highlightOutput: 255, midtoneInput: 1 };
      return {
        kind: 'levels',
        params: {
          inBlack: channel.shadowInput,
          inWhite: channel.highlightInput,
          gamma: channel.midtoneInput,
          outBlack: channel.shadowOutput,
          outWhite: channel.highlightOutput,
        },
      };
    }
    case 'hue/saturation': {
      const m = adj.master;
      return {
        kind: 'hsl',
        params: { hue: m?.hue ?? 0, saturation: m?.saturation ?? 0, lightness: m?.lightness ?? 0 },
      };
    }
    case 'color balance': {
      const shadows = adj.shadows ?? { cyanRed: 0, magentaGreen: 0, yellowBlue: 0 };
      const mid = adj.midtones ?? { cyanRed: 0, magentaGreen: 0, yellowBlue: 0 };
      const high = adj.highlights ?? { cyanRed: 0, magentaGreen: 0, yellowBlue: 0 };
      return {
        kind: 'colorBalance',
        params: {
          shadowR: shadows.cyanRed,
          shadowG: shadows.magentaGreen,
          shadowB: shadows.yellowBlue,
          midR: mid.cyanRed,
          midG: mid.magentaGreen,
          midB: mid.yellowBlue,
          highR: high.cyanRed,
          highG: high.magentaGreen,
          highB: high.yellowBlue,
          preserveLuminosity: adj.preserveLuminosity === false ? 0 : 1,
        },
      };
    }
    case 'color lookup':
      warnings.push(`${layerName}（颜色查找调整图层暂不支持解析具体 LUT 数据，已替换成恒等曲线）`);
      return identityCurves();
    case 'brightness/contrast':
      return {
        kind: 'brightnessContrast',
        params: { brightness: adj.brightness ?? 0, contrast: adj.contrast ?? 0 },
      };
    case 'black & white':
      warnings.push(`${layerName}（黑白调整图层：Photoshop 用红/黄/绿/青/蓝/洋红六个滑杆，本项目只有红/绿/蓝三个，已丢弃黄/青/洋红，效果是近似）`);
      return {
        kind: 'blackAndWhite',
        params: { r: adj.reds ?? 40, g: adj.greens ?? 40, b: adj.blues ?? 20 },
      };
    case 'photo filter':
      return {
        kind: 'photoFilter',
        params: {
          ...(() => {
            const [r, g, b] = colorToRgb(adj.color, [255, 157, 0]);
            return { r, g, b };
          })(),
          density: adj.density ?? 25,
          preserveLuminosity: adj.preserveLuminosity === false ? 0 : 1,
        },
      };
    case 'channel mixer': {
      const red = adj.red ?? { red: 100, green: 0, blue: 0, constant: 0 };
      const green = adj.green ?? { red: 0, green: 100, blue: 0, constant: 0 };
      const blue = adj.blue ?? { red: 0, green: 0, blue: 100, constant: 0 };
      return {
        kind: 'channelMixer',
        params: {
          rr: red.red,
          rg: red.green,
          rb: red.blue,
          rc: red.constant,
          gr: green.red,
          gg: green.green,
          gb: green.blue,
          gc: green.constant,
          br: blue.red,
          bg: blue.green,
          bb: blue.blue,
          bc: blue.constant,
        },
      };
    }
    case 'gradient map': {
      const stops = adj.colorStops;
      const color1 = stops !== undefined && stops[0] !== undefined ? colorToRgb(stops[0].color, [0, 0, 0]) : [0, 0, 0] as const;
      const last = stops !== undefined ? stops[stops.length - 1] : undefined;
      const color2 = last !== undefined ? colorToRgb(last.color, [255, 255, 255]) : [255, 255, 255] as const;
      return { kind: 'gradientMap', params: { color1: [...color1], color2: [...color2] } };
    }
    case 'posterize':
      return { kind: 'posterize', params: { levels: adj.levels ?? 4 } };
    case 'threshold':
      return { kind: 'threshold', params: { level: adj.level ?? 128 } };
    case 'vibrance':
      return { kind: 'vibrance', params: { amount: adj.vibrance ?? 0 } };
    case 'invert':
      // 反相对每个通道来说恰好是"两点曲线首尾互换"，是精确等价映射，不是近似
      return identityCurvesInverted();
    case 'exposure':
      warnings.push(`${layerName}（曝光度调整图层：Photoshop 的曝光度算法跟本项目"基础调整"面板的曝光滑杆不是同一套曲线，硬凑会在后续调整时产生视觉漂移，已替换成恒等曲线）`);
      return identityCurves();
    case 'selective color':
      warnings.push(`${layerName}（可选颜色调整图层，本项目没有对应的调整种类，已替换成恒等曲线）`);
      return identityCurves();
    default:
      warnings.push(`${layerName}（未知调整图层类型「${(adj as { type: string }).type}」，已替换成恒等曲线）`);
      return identityCurves();
  }
}

function identityCurvesInverted(): Adjustment {
  return { kind: 'curves', params: { points: [0, 255, 255, 0] } };
}

/* ------------------------------------------------------------------ */
/* 文本                                                                 */
/* ------------------------------------------------------------------ */

/** 只有单一样式、无分段、非竖排、无变形才能安全重建成可编辑文本；否则返回 null 交给调用方栅格化。 */
function tryConvertText(node: PsdDecodedLayer): TextLayer['runs'] | null {
  const text = node.text;
  if (text === undefined) return null;
  if (text.orientation === 'vertical') return null;
  if (text.warp !== undefined && text.warp.style !== undefined && text.warp.style !== 'none') return null;
  if (text.styleRuns !== undefined && text.styleRuns.length > 1) return null;

  const style = text.style;
  const [r, g, b] = colorToRgb(style?.fillColor, [0, 0, 0]);
  return [
    {
      text: text.text,
      fontFamily: style?.font?.name ?? 'sans-serif',
      fontSize: style?.fontSize ?? 12,
      fill: { type: 'solid', color: rgbToHex([r, g, b]) },
      letterSpacing: 0,
      lineHeight: 1.2,
    },
  ];
}

/* ------------------------------------------------------------------ */
/* 主转换                                                                */
/* ------------------------------------------------------------------ */

interface ConvertContext {
  readonly tiles: TileStore;
  readonly warnings: string[];
  layerCount: number;
  idCounter: number;
}

function nextLayerId(ctx: ConvertContext, docId: string): LayerId {
  ctx.idCounter += 1;
  return layerId(`${docId}:psd-${ctx.idCounter}`);
}

function convertNode(node: PsdDecodedLayer, docId: string, depth: number, ctx: ConvertContext): Layer {
  ctx.layerCount += 1;
  if (ctx.layerCount > MAX_LAYER_COUNT) {
    throw new Error(`图层数量超过导入上限（${MAX_LAYER_COUNT}），可能是畸形文件`);
  }
  if (depth > MAX_NESTING_DEPTH) {
    throw new Error(`图层分组嵌套超过导入上限（${MAX_NESTING_DEPTH}），可能是畸形文件`);
  }

  const name = node.name !== undefined && node.name.length > 0 ? node.name : '未命名图层';
  const id = nextLayerId(ctx, docId);
  const base = {
    ...layerDefaults(id, name),
    visible: node.hidden !== true,
    // ag-psd 的 readPsd 已经把 opacity 换算成 0–1 浮点（psdReader.js:
    // `layer.opacity = readUint8(reader) / 0xff`），不需要再除一次 255——
    // 之前以为它是 0-255 整数，被这条真实的往返测试当场揪出来
    opacity: node.opacity ?? 1,
  };

  if (node.children !== undefined) {
    const passThrough = node.blendMode === 'pass through';
    const group: GroupLayer = {
      ...base,
      blendMode: passThrough ? 'normal' : mapBlendMode(node.blendMode, ctx.warnings, name),
      kind: 'group',
      passThrough,
      collapsed: node.opened === false,
      children: [...node.children].reverse().map((child) => convertNode(child, docId, depth + 1, ctx)),
    };
    return group;
  }

  const blendMode = mapBlendMode(node.blendMode, ctx.warnings, name);

  // 调整图层：没有自己的像素，不走栅格化这条路
  if (node.adjustment !== undefined) {
    return {
      ...base,
      blendMode,
      kind: 'adjustment',
      adjustment: convertAdjustment(node, ctx.warnings, name),
    };
  }

  const rect = boundsOf(node);
  const mask = convertMask(node, ctx.tiles, id, ctx.warnings, name);
  const effects = convertEffects(node, ctx.warnings, name);

  // 文本：只在简单情形下重建成可编辑图层，否则落到下面栅格化
  if (node.text !== undefined) {
    const runs = tryConvertText(node);
    if (runs !== null) {
      const textLayer: TextLayer = {
        ...base,
        blendMode,
        kind: 'text',
        runs,
        box: { x: 0, y: 0, width: rect.width, height: rect.height },
        writingMode: 'horizontal',
        ...(mask === undefined ? {} : { mask }),
        effects,
      };
      return textLayer;
    }
    ctx.warnings.push(`${name}（复杂文本格式——多段样式/竖排/文字变形，已栅格化，不再可编辑）`);
  } else if (node.placedLayer !== undefined) {
    ctx.warnings.push(`${name}（智能对象，已栅格化，不再是非破坏性的滤镜链）`);
  } else if (node.vectorMask !== undefined) {
    ctx.warnings.push(`${name}（形状/矢量图层，已栅格化，路径不再可编辑）`);
  }

  const imageData = node.imageData;
  const pixels =
    imageData === undefined
      ? new Uint8Array(0)
      : toUint8Array(imageData.data);
  writeRectPixelsToTiles(ctx.tiles, id, rect, pixels);

  const raster: RasterLayer = {
    ...base,
    blendMode,
    kind: 'raster',
    tiles: ctx.tiles.handle(id),
    bounds: rect,
    effects,
    ...(mask === undefined ? {} : { mask }),
  };
  return raster;
}

/** 打开一份 PSD 文件，转换成本项目的 `Document`。像素写进 `tiles`。 */
export async function importPsd(
  bytes: Uint8Array,
  tiles: TileStore,
  options: ImportPsdOptions,
): Promise<ImportPsdResult> {
  if (bytes.length > MAX_FILE_BYTES) {
    throw new Error(`文件过大（${Math.round(bytes.length / 1024 / 1024)}MB），超过导入上限`);
  }

  let psd: PsdDecoded;
  try {
    psd = await decodePsdBytes(bytes);
  } catch (error) {
    throw new Error(
      `PSD 解析失败，文件损坏或包含不支持的特性：${error instanceof Error ? error.message : String(error)}`,
      { cause: error },
    );
  }

  assertSupportedColorMode(psd);

  const { width, height } = psd;
  const ctx: ConvertContext = { tiles, warnings: [], layerCount: 0, idCounter: 0 };

  let children: Layer[];
  try {
    children = (psd.children ?? []).slice().reverse().map((node) => convertNode(node, options.id, 1, ctx));
  } catch (error) {
    if (error instanceof Error && error.message.includes('导入上限')) throw error;
    throw new Error(
      `PSD 解析失败，文件损坏或包含不支持的特性：${error instanceof Error ? error.message : String(error)}`,
      { cause: error },
    );
  }

  const base = createDocument({
    id: options.id,
    width,
    height,
    title: options.fileName,
    createdAt: options.now(),
  });
  const document: Document = {
    ...base,
    meta: { ...base.meta, sourceFileName: options.fileName },
    root: { ...base.root, children },
  };
  return { document, warnings: ctx.warnings };
}
